import React from 'react';
import { useTranslation } from 'react-i18next';
import { createUseStyles } from 'react-jss';
import { Theme } from '@app/theme';
import { IndexedParticipation, StudyType } from '@app/api';
import {
  NotificationPin,
  QuestionTooltip,
  SquareButton,
  TextLoadingPlaceholder,
  useSessionUnreadMessageByParticipation,
} from '@app/core';
import { useParticipationTableContext } from './ParticipationTableContext';
import { ActionButton } from '../ActionButton';
import {
  ActionEnum,
  ActionsConfig,
} from '../../hooks/useParticipationTableActions';

const useStyles = createUseStyles((theme: Theme) => ({
  actionsCell: {
    minWidth: '100%',
    textAlign: 'right',
  },
  actionContainerHeader: {
    display: 'flex',
    gap: theme.marginBase,
    alignItems: 'center',
    justifyContent: 'flex-end',
  },
  actionContainer: {
    display: 'flex',
    gap: theme.marginBase * 2,
  },
  action: {
    minHeight: 0,
    minWidth: 0,
  },
  actionContent: {
    padding: 0,
  },
  withNotificationPin: {
    position: 'relative',
    height: theme.iconSize.large,
    width: theme.iconSize.large,
  },
}));

export const ActionsHeader = () => {
  const { t } = useTranslation();
  const { tab } = useParticipationTableContext();
  const classes = useStyles();

  return (
    <th key="actions" className={classes.actionsCell}>
      <div className={classes.actionContainerHeader}>
        {t('sessions.details.participantsDetails.columns.actions')}
        {tab === 'participated' && (
          <QuestionTooltip
            placement="left"
            title={t(
              'participants.list.selectionFooter.tooltip.report_validate',
            )}
          />
        )}
      </div>
    </th>
  );
};

export const ActionsCell = ({
  participation,
  actionsConfig,
}: {
  participation: IndexedParticipation;
  actionsConfig: ActionsConfig;
}) => {
  const { session } = useParticipationTableContext();
  const classes = useStyles();

  const unreadMessageCount = useSessionUnreadMessageByParticipation(
    session.id,
    participation?.id,
  );

  return (
    <td key="actions" className={classes.actionsCell} data-no-row-click>
      <div className={classes.actionContainer}>
        <SquareButton
          to={`part/${participation.id}`}
          className={classes.action}
          icon="ri-eye-line"
          color="blue"
          variant="muted"
          small
          smallIcon
        />
        {session.studyType === StudyType.MODERATED && (
          <div className={classes.withNotificationPin}>
            <SquareButton
              to={`part/${participation.id}?tab=MESSAGES`}
              className={classes.action}
              icon="ri-chat-3-line"
              color="inkSecondary"
              variant="muted"
              small
              smallIcon
            />
            {unreadMessageCount > 0 && <NotificationPin />}
          </div>
        )}
        {actionsConfig &&
          Object.keys(actionsConfig).map((key) => {
            const tabAction = actionsConfig![key as ActionEnum];
            if (
              !tabAction.supports.includes(session.studyType) ||
              !tabAction.status.includes(participation.status) ||
              (key === ActionEnum.RESTORE_UNMODERATED &&
                participation.unreachable)
            ) {
              return null;
            }
            return (
              <ActionButton
                action={tabAction}
                key={key}
                participation={participation}
              />
            );
          })}
      </div>
    </td>
  );
};

export const ActionsLoader = () => {
  const { t } = useTranslation();
  return (
    <td key="actions">
      <TextLoadingPlaceholder
        sample={t('sessions.details.participantsDetails.columns.actions')}
      />
    </td>
  );
};
